import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Save, Loader2, ShieldCheck, Tag, AlertCircle, Flag, User } from 'lucide-react';
import { FeedbackResponseDto } from '../Types/FeedbackType';
import { updateFeedbackByAdmin } from '../Services/AdminService';

interface Props {
  isOpen: boolean;
  feedback: FeedbackResponseDto | null;
  currentUserId: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}

interface FormState {
  status: string;
  resolvedMessage: string;
}

const emptyForm: FormState = {
  status: 'IN_PROGRESS',
  resolvedMessage: '',
};

const STATUS_OPTIONS = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'REOPEN'];

const PRIORITY_STYLES: Record<string, string> = {
  LOW: 'border-slate-700 bg-slate-950 text-slate-400',
  MEDIUM: 'border-sky-500/30 bg-sky-500/10 text-sky-400',
  HIGH: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  CRITICAL: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
};

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400';

const prettify = (value?: string) =>
  value ? value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const AdminFeedbackFormModal = ({
  isOpen,
  feedback,
  currentUserId,
  onClose,
  onSaved,
}: Props) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!isOpen) return;

    setFormError('');
    setSaving(false);

    if (feedback) {
      setForm({
        status: feedback.status || 'IN_PROGRESS',
        resolvedMessage: feedback.resolvedMessage || '',
      });
    } else {
      setForm(emptyForm);
    }
  }, [isOpen, feedback]);

  const handleSubmit = async () => {
    if (!feedback) {
      setFormError('No feedback selected.');
      return;
    }

    if (!form.resolvedMessage.trim()) {
      setFormError('Resolution message is required.');
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      const response = await updateFeedbackByAdmin(
        {
          feedbackId: feedback.id,
          resolvedMessage: form.resolvedMessage.trim(),
          status: form.status,
        },
        currentUserId
      );

      if (response.success) {
        onSaved('Feedback updated successfully.');
        onClose();
      } else {
        setFormError(response.message || 'Could not update the feedback.');
      }
    } catch (err) {
      console.error('Failed to update feedback', err);
      setFormError('An unexpected error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
            className="my-8 w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                  <ShieldCheck size={18} />
                </span>
                <div>
                  <h2 className="text-base font-black tracking-tight text-slate-100">Review Feedback</h2>
                  <p className="text-[11px] font-semibold text-slate-500">
                    Update the status and resolution for this request
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-4 px-6 py-5">
              {formError && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs font-bold text-rose-300">
                  {formError}
                </div>
              )}

              {feedback && (
                <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                  <p className="text-sm font-bold text-slate-100">{feedback.subject || '—'}</p>
                  {feedback.message && (
                    <p className="mt-1 line-clamp-2 text-[11px] text-slate-500">{feedback.message}</p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                      <User size={12} className="text-slate-500" />
                      {feedback.reportedName || '—'}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                      <Tag size={12} className="text-slate-500" />
                      {prettify(feedback.category)}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                        PRIORITY_STYLES[feedback.feedbackPriority] ?? 'border-slate-700 bg-slate-950 text-slate-400'
                      }`}
                    >
                      <Flag size={11} /> {feedback.feedbackPriority || '—'}
                    </span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    <AlertCircle size={11} className="mr-1 inline" /> Status
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) => set('status', e.target.value)}
                    className={fieldClass}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option} value={option} className="bg-slate-900 text-slate-100">
                        {prettify(option)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Resolution Message *</label>
                  <textarea
                    rows={5}
                    value={form.resolvedMessage}
                    onChange={(e) => set('resolvedMessage', e.target.value)}
                    placeholder="Explain the resolution or the next steps for this request..."
                    className={`${fieldClass} resize-none`}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-slate-800 px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                {saving ? 'Saving' : 'Save Changes'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdminFeedbackFormModal;
